import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import AnnouncementBanner from '../../components/ui/AnnouncementBanner';
import Icon from '../../components/AppIcon';
import { paidAuditService } from '../../services/paidAuditService';
import { invoiceGenerator } from '../../utils/invoiceGenerator';
import PaymentMethodCard from './components/PaymentMethodCard';
import SubscriptionPanel from './components/SubscriptionPanel';
import InvoiceHistoryTable from './components/InvoiceHistoryTable';
import RefundSupportForm from './components/RefundSupportForm';
import toast from 'react-hot-toast';

const GRAD        = 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)';
const GRAD_SHADOW = '0 4px 16px rgba(0,74,173,0.35)';

/* ─── Styles ─────────────────────────────────────────────────────────────── */
const STYLES = `
  .bs-root * { box-sizing: border-box; }
  .bs-root {
    min-height: 100vh;
    background: var(--background);
    font-family: inherit;
    padding-top: 58px;
  }

  .bs-inner {
    max-width: 1280px;
    margin: 0 auto;
    padding: 32px 24px 64px;
  }

  /* ── Page header ── */
  .bs-page-header { margin-bottom: 28px; }
  .bs-page-header h1 {
    font-family: 'Sansation', system-ui, sans-serif;
    font-size: clamp(22px, 5vw, 32px);
    font-weight: 700;
    color: var(--foreground, #0f172a);
    margin: 0 0 8px;
    letter-spacing: -0.02em;
  }
  .bs-page-header p {
    font-size: 15px;
    color: var(--muted-foreground, #64748b);
    margin: 0;
  }

  /* ── Security badge ── */
  .bs-security {
    background: rgba(0,74,173,0.05);
    border: 1px solid rgba(0,74,173,0.18);
    border-radius: 12px;
    padding: 14px 18px;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    gap: 14px;
  }
  .bs-security-icon {
    width: 40px; height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .bs-security-title {
    font-size: 13px; font-weight: 600;
    color: var(--foreground, #0f172a);
    margin: 0 0 2px;
  }
  .bs-security-sub {
    font-size: 12px;
    color: var(--muted-foreground, #64748b);
    margin: 0;
    line-height: 1.5;
  }

  /* ── Tabs ── */
  .bs-tabs-bar {
    border-bottom: 1px solid var(--border, #e2e8f0);
    margin-bottom: 24px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  .bs-tabs-bar::-webkit-scrollbar { display: none; }

  .bs-tabs-row {
    display: flex;
    gap: 2px;
    min-width: max-content;
  }

  .bs-tab {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 10px 16px;
    border: none;
    border-bottom: 2px solid transparent;
    background: transparent;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    color: var(--muted-foreground, #64748b);
    font-family: inherit;
    white-space: nowrap;
    transition: color .15s, border-color .15s;
    margin-bottom: -1px;
  }
  .bs-tab:hover { color: var(--foreground, #0f172a); }
  .bs-tab.active {
    font-weight: 700;
    color: #004AAD;
    border-bottom-color: #004AAD;
  }

  /* ── Stat cards grid ── */
  .bs-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }
  .bs-stat-card {
    background: var(--card, #fff);
    border: 1px solid var(--border, #e2e8f0);
    border-radius: 16px;
    padding: 22px 22px 20px;
  }
  .bs-stat-icon-row {
    display: flex; align-items: center; gap: 12px;
    margin-bottom: 14px;
  }
  .bs-stat-icon {
    width: 40px; height: 40px;
    border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .bs-stat-label {
    font-size: 13px;
    color: var(--muted-foreground, #64748b);
  }
  .bs-stat-value {
    font-family: 'Sansation', system-ui, sans-serif;
    font-size: clamp(22px, 4vw, 30px);
    font-weight: 700;
    color: var(--foreground, #0f172a);
    margin: 0 0 4px;
    letter-spacing: -0.02em;
  }
  .bs-stat-sub {
    font-size: 12px;
    color: var(--muted-foreground, #64748b);
    margin: 0;
  }

  /* ── Recent invoices card ── */
  .bs-invoices-card {
    background: var(--card, #fff);
    border: 1px solid var(--border, #e2e8f0);
    border-radius: 16px;
    padding: 24px 24px 28px;
  }
  .bs-invoices-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;
    gap: 12px;
    flex-wrap: wrap;
  }
  .bs-invoices-title {
    font-family: 'Sansation', system-ui, sans-serif;
    font-size: 18px; font-weight: 700;
    color: var(--foreground, #0f172a);
    margin: 0;
    display: flex; align-items: center; gap: 8px;
  }
  .bs-view-all {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 7px 14px; border-radius: 8px;
    border: 1px solid var(--border, #e2e8f0);
    background: transparent;
    color: var(--foreground, #0f172a);
    font-size: 12px; font-weight: 600;
    cursor: pointer; font-family: inherit;
    transition: border-color .15s, color .15s;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .bs-view-all:hover { border-color: #004AAD; color: #004AAD; }

  /* invoice row */
  .bs-inv-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 14px;
    background: var(--muted, #f8fafc);
    border-radius: 10px;
    border: 1px solid var(--border, #e2e8f0);
    gap: 12px;
    transition: border-color .15s;
  }
  .bs-inv-row:hover { border-color: rgba(0,74,173,0.30); }
  .bs-inv-left  { display: flex; align-items: center; gap: 12px; min-width: 0; flex: 1; }
  .bs-inv-right { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
  .bs-inv-title {
    font-size: 13px; font-weight: 600;
    color: var(--foreground, #0f172a);
    margin: 0 0 2px;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .bs-inv-date {
    font-size: 11px; color: var(--muted-foreground, #64748b); margin: 0;
  }
  .bs-inv-amount {
    font-size: 14px; font-weight: 700;
    color: var(--foreground, #0f172a);
    white-space: nowrap;
  }
  .bs-inv-dl {
    padding: 5px 8px; border-radius: 7px;
    border: 1px solid var(--border, #e2e8f0);
    background: transparent; cursor: pointer;
    color: var(--muted-foreground, #64748b);
    display: flex; align-items: center;
    transition: color .15s, border-color .15s;
  }
  .bs-inv-dl:hover { color: #004AAD; border-color: #004AAD; }

  .bs-inv-list { display: flex; flex-direction: column; gap: 10px; }

  .bs-inv-empty { text-align: center; padding: 32px 0; }
  .bs-inv-empty p { font-size: 14px; color: var(--muted-foreground, #64748b); margin: 12px 0 0; }

  .bs-inv-loading {
    display: flex; align-items: center; justify-content: center;
    padding: 32px 0; gap: 10px;
  }
  .bs-inv-loading span {
    font-size: 13px; color: var(--muted-foreground, #64748b);
  }

  /* ── Overview column ── */
  .bs-overview { display: flex; flex-direction: column; gap: 24px; }

  /* ─────────── MOBILE ─────────── */
  @media (max-width: 900px) {
    /* stat cards: 2 columns on tablet */
    .bs-stats { grid-template-columns: repeat(2, 1fr); }
  }

  @media (max-width: 768px) {
    .bs-inner { padding: 20px 16px 48px; }

    .bs-page-header { margin-bottom: 18px; }
    .bs-page-header h1 { font-size: 22px; }
    .bs-page-header p { font-size: 13px; }

    /* stat cards: 2 columns on mobile */
    .bs-stats { grid-template-columns: repeat(2, 1fr); gap: 12px; }
    .bs-stat-card { padding: 16px 14px; border-radius: 14px; }
    .bs-stat-icon-row { gap: 8px; margin-bottom: 10px; }
    .bs-stat-icon { width: 34px; height: 34px; border-radius: 8px; }
    .bs-stat-label { font-size: 12px; }
    .bs-stat-value { font-size: 22px; }
    .bs-stat-sub { font-size: 11px; }

    .bs-invoices-card { padding: 18px 14px 22px; border-radius: 14px; }
    .bs-invoices-title { font-size: 16px; }

    .bs-security { padding: 12px 14px; gap: 10px; }
    .bs-security-icon { width: 34px; height: 34px; }
    .bs-security-title { font-size: 12px; }
    .bs-security-sub { font-size: 11px; }

    .bs-tab { padding: 9px 12px; font-size: 12px; gap: 5px; }

    .bs-inv-row { padding: 10px 12px; }
    .bs-inv-title { font-size: 12px; }
    .bs-inv-amount { font-size: 13px; }

    .bs-overview { gap: 16px; }
  }

  @media (max-width: 480px) {
    .bs-inner { padding: 16px 12px 40px; }

    /* stat cards: full width on tiny screens */
    .bs-stats { grid-template-columns: 1fr 1fr; gap: 10px; }

    /* invoice row: stack on very small */
    .bs-inv-row { flex-wrap: wrap; }
    .bs-inv-right { margin-left: auto; }

    .bs-invoices-header { flex-direction: column; align-items: flex-start; gap: 8px; }
    .bs-view-all { align-self: flex-start; }
  }

  @media (max-width: 360px) {
    .bs-inner  { padding: 12px 10px 36px; }
    .bs-stats  { grid-template-columns: 1fr; }
    .bs-tab    { padding: 8px 10px; font-size: 11px; }
  }
`;

/* ─── BillingScreen ───────────────────────────────────────────────────────── */
const BillingScreen = () => {
  const { user, userProfile } = useAuth();
  const [paymentHistory,    setPaymentHistory]    = useState([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(true);
  const [activeTab,         setActiveTab]         = useState('overview');

  useEffect(() => {
    if (user?.id) fetchPaymentHistory();
  }, [user?.id]);

  const fetchPaymentHistory = async () => {
    try {
      setIsLoadingPayments(true);
      const payments = await paidAuditService?.getUserPaymentOrders(user?.id);
      setPaymentHistory(payments || []);
    } catch (error) {
      console.error('Error fetching payment history:', error);
      toast?.error('Failed to load payment history');
    } finally {
      setIsLoadingPayments(false);
    }
  };

  const handleDownloadInvoice = (payment) => {
    try   { invoiceGenerator?.downloadInvoice(payment, userProfile); toast?.success('Invoice downloaded successfully'); }
    catch { toast?.error('Failed to download invoice'); }
  };

  const handleBulkExport = () => {
    try {
      const captured = paymentHistory?.filter(p => p?.payment_status === 'captured');
      if (!captured?.length) { toast?.error('No paid invoices to export'); return; }
      captured.forEach(payment => invoiceGenerator?.downloadInvoice(payment, userProfile));
      toast?.success(`Exported ${captured.length} invoices`);
    } catch { toast?.error('Failed to export invoices'); }
  };

  const totalSpent = paymentHistory
    ?.filter(p => p?.payment_status === 'captured')
    ?.reduce((sum, p) => sum + (p?.amount || 0), 0);

  const tabs = [
    { id: 'overview',        label: 'Overview',        icon: 'LayoutDashboard' },
    { id: 'payment-methods', label: 'Payment Methods', icon: 'CreditCard'      },
    { id: 'invoices',        label: 'Invoices',        icon: 'FileText'        },
    { id: 'support',         label: 'Support',         icon: 'HelpCircle'      },
  ];

  const statCards = [
    {
      icon: 'DollarSign', iconBg: 'rgba(0,74,173,0.10)', iconColor: '#004AAD',
      label: 'Total Spent',
      value: totalSpent > 0 ? `$${(totalSpent / 100).toFixed(2)}` : '$0.00',
      sub: 'All-time',
    },
    {
      icon: 'CheckCircle2', iconBg: 'rgba(22,163,74,0.10)', iconColor: '#16a34a',
      label: 'Paid Invoices',
      value: paymentHistory?.filter(p => p?.payment_status === 'captured')?.length ?? 0,
      sub: 'Successful payments',
    },
    {
      icon: 'Clock', iconBg: 'rgba(245,158,11,0.10)', iconColor: '#d97706',
      label: 'Pending',
      value: paymentHistory?.filter(p => p?.payment_status === 'pending')?.length ?? 0,
      sub: 'Awaiting payment',
    },
  ];

  return (
    <>
      <style>{STYLES}</style>
      <Helmet>
        <title>Billing - myDeckAudit</title>
        <meta name="description" content="Manage your billing, payment methods, and subscription" />
      </Helmet>

      <RoleBasedHeader />
      <AnnouncementBanner />

      <div className="bs-root">
        <div className="bs-inner">

          {/* Page header */}
          <div className="bs-page-header">
            <h1>Billing &amp; Payments</h1>
            <p>Manage your payment methods, subscription, and invoices</p>
          </div>

          {/* Security badge */}
          <div className="bs-security">
            <div className="bs-security-icon">
              <Icon name="Shield" size={18} color="#ffffff" />
            </div>
            <div>
              <p className="bs-security-title">Secure Payment Processing</p>
              <p className="bs-security-sub">PCI DSS compliant · 256-bit SSL encryption · Powered by Razorpay</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="bs-tabs-bar">
            <div className="bs-tabs-row">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  className={`bs-tab${activeTab === tab.id ? ' active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <Icon name={tab.icon} size={15} color={activeTab === tab.id ? '#004AAD' : undefined} />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* ── Overview ── */}
          {activeTab === 'overview' && (
            <div className="bs-overview">

              {/* Stat cards */}
              <div className="bs-stats">
                {statCards.map(({ icon, iconBg, iconColor, label, value, sub }) => (
                  <div key={label} className="bs-stat-card">
                    <div className="bs-stat-icon-row">
                      <div className="bs-stat-icon" style={{ background: iconBg }}>
                        <Icon name={icon} size={20} color={iconColor} />
                      </div>
                      <span className="bs-stat-label">{label}</span>
                    </div>
                    <p className="bs-stat-value">{value}</p>
                    <p className="bs-stat-sub">{sub}</p>
                  </div>
                ))}
              </div>

              {/* Subscription panel */}
              <SubscriptionPanel userProfile={userProfile} paymentHistory={paymentHistory} />

              {/* Recent invoices */}
              <div className="bs-invoices-card">
                <div className="bs-invoices-header">
                  <h2 className="bs-invoices-title">
                    <Icon name="FileText" size={18} color="#004AAD" />
                    Recent Invoices
                  </h2>
                  <button className="bs-view-all" onClick={() => setActiveTab('invoices')}>
                    <Icon name="Eye" size={13} /> View All
                  </button>
                </div>

                {isLoadingPayments ? (
                  <div className="bs-inv-loading">
                    <Icon name="Loader2" size={22} color="#004AAD" className="animate-spin" />
                    <span>Loading invoices...</span>
                  </div>
                ) : paymentHistory?.length > 0 ? (
                  <div className="bs-inv-list">
                    {paymentHistory.slice(0, 3).map(payment => (
                      <div key={payment?.id} className="bs-inv-row">
                        <div className="bs-inv-left">
                          <Icon name="FileText" size={15} color="#004AAD" style={{ flexShrink: 0 }} />
                          <div style={{ minWidth: 0 }}>
                            <p className="bs-inv-title">Paid Deck Audit</p>
                            <p className="bs-inv-date">
                              {new Date(payment?.paid_at || payment?.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </p>
                          </div>
                        </div>
                        <div className="bs-inv-right">
                          <span className="bs-inv-amount">
                            {payment?.currency === 'INR' ? '₹' : '$'}{(payment?.amount / 100).toFixed(2)}
                          </span>
                          {payment?.payment_status === 'captured' && (
                            <button className="bs-inv-dl" onClick={() => handleDownloadInvoice(payment)}>
                              <Icon name="Download" size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bs-inv-empty">
                    <Icon name="FileText" size={40} color="var(--muted-foreground, #94a3b8)" />
                    <p>No invoices yet</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'payment-methods' && <PaymentMethodCard />}

          {activeTab === 'invoices' && (
            <InvoiceHistoryTable
              paymentHistory={paymentHistory}
              isLoading={isLoadingPayments}
              onDownloadInvoice={handleDownloadInvoice}
              onBulkExport={handleBulkExport}
              userProfile={userProfile}
            />
          )}

          {activeTab === 'support' && <RefundSupportForm paymentHistory={paymentHistory} />}

        </div>
      </div>
    </>
  );
};

export default BillingScreen;